/**
 * Agent Definitions Controller
 *
 * Manages state and gateway RPC calls for dynamic DB-backed agent definitions.
 */

// --------------- Types ---------------

export type AgentBindingMatch = {
  channel: string;
  accountId?: string;
  peer?: { kind: string; id: string };
  guildId?: string;
  teamId?: string;
};

export type AgentModelJsonb = { primary?: string; fallbacks?: string[] };

export type AgentDefinitionEntry = {
  agentId: string;
  name: string;
  description?: string;
  projectId?: string;
  projectName?: string;
  model?: AgentModelJsonb | null;
  systemPromptOverride?: string;
  skills?: string[];
  toolPolicy?: Record<string, unknown>;
  bindings?: AgentBindingMatch[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type IdentityFileInfo = {
  fileName: string;
  hasOverride: boolean;
  content: string;
  source: "db-override" | "default-template";
};

// --------------- State ---------------

export interface AgentDefinitionsState {
  client: { request<T>(method: string, params: unknown): Promise<T> } | null;
  connected: boolean;
  igProjects: Array<{ id: string; name: string; slug?: string }>;

  agentDefs: AgentDefinitionEntry[];
  agentDefsLoading: boolean;
  agentDefsError: string | null;
  agentDefCreateMode: boolean;
  agentDefEditingId: string | null;
  agentDefForm: Partial<AgentDefinitionEntry> | null;

  // Identity file overrides
  agentDefIdFiles: IdentityFileInfo[];
  agentDefIdFilesLoading: boolean;
  agentDefIdFileActive: string | null;
  agentDefIdFileDrafts: Record<string, string>;
  agentDefIdFileSaving: boolean;
}

export const AGENT_DEFS_STATE_DEFAULTS: Omit<
  AgentDefinitionsState,
  "client" | "connected" | "igProjects"
> = {
  agentDefs: [],
  agentDefsLoading: false,
  agentDefsError: null,
  agentDefCreateMode: false,
  agentDefEditingId: null,
  agentDefForm: null,
  agentDefIdFiles: [],
  agentDefIdFilesLoading: false,
  agentDefIdFileActive: null,
  agentDefIdFileDrafts: {},
  agentDefIdFileSaving: false,
};

// --------------- Helpers ---------------

function friendlyError(err: unknown): string {
  const msg = String(err).toLowerCase();
  if (msg.includes("method not found") || msg.includes("not registered")) {
    return "Agent definition methods not available. Ensure the igloobot-core plugin is loaded.";
  }
  return String(err);
}

// --------------- CRUD ---------------

type ListResult = { ok: boolean; data?: AgentDefinitionEntry[]; error?: string };
type ItemResult = { ok: boolean; data?: AgentDefinitionEntry; error?: string };
type SimpleResult = { ok: boolean; error?: string };

export async function loadAgentDefinitions(state: AgentDefinitionsState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.agentDefsLoading = true;
  state.agentDefsError = null;
  try {
    const res = await state.client.request<ListResult>("igloobot.agents.list", {});
    if (res.ok && res.data) {
      state.agentDefs = res.data;
    } else {
      state.agentDefsError = res.error ?? "Failed to load agent definitions";
    }
  } catch (err) {
    state.agentDefsError = friendlyError(err);
  } finally {
    state.agentDefsLoading = false;
  }
}

export async function createAgentDefinition(
  state: AgentDefinitionsState,
  onMutate?: () => void,
): Promise<void> {
  if (!state.client || !state.connected || !state.agentDefForm) {
    return;
  }
  state.agentDefsLoading = true;
  try {
    const form = state.agentDefForm;
    const params: Record<string, unknown> = {
      name: form.name ?? "",
    };
    if (form.description) {
      params.description = form.description;
    }
    if (form.projectId) {
      params.projectId = form.projectId;
    }
    if (form.model && typeof form.model === "object" && form.model.primary) {
      params.model = form.model;
    }
    if (form.systemPromptOverride) {
      params.systemPromptOverride = form.systemPromptOverride;
    }
    if (form.skills?.length) {
      params.skills = form.skills;
    }
    if (form.bindings?.length) {
      params.bindings = form.bindings;
    }

    const res = await state.client.request<ItemResult>("igloobot.agents.create", params);
    if (res.ok) {
      state.agentDefCreateMode = false;
      state.agentDefForm = null;
      await loadAgentDefinitions(state);
      onMutate?.();
    } else {
      state.agentDefsError = res.error ?? "Failed to create agent definition";
    }
  } catch (err) {
    state.agentDefsError = friendlyError(err);
  } finally {
    state.agentDefsLoading = false;
  }
}

export async function updateAgentDefinition(
  state: AgentDefinitionsState,
  onMutate?: () => void,
): Promise<void> {
  if (!state.client || !state.connected || !state.agentDefEditingId || !state.agentDefForm) {
    return;
  }
  state.agentDefsLoading = true;
  try {
    const res = await state.client.request<ItemResult>("igloobot.agents.update", {
      agentId: state.agentDefEditingId,
      ...state.agentDefForm,
    });
    if (res.ok) {
      state.agentDefEditingId = null;
      state.agentDefForm = null;
      await loadAgentDefinitions(state);
      onMutate?.();
    } else {
      state.agentDefsError = res.error ?? "Failed to update agent definition";
    }
  } catch (err) {
    state.agentDefsError = friendlyError(err);
  } finally {
    state.agentDefsLoading = false;
  }
}

export async function deleteAgentDefinition(
  state: AgentDefinitionsState,
  agentId: string,
  onMutate?: () => void,
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    const res = await state.client.request<SimpleResult>("igloobot.agents.delete", { agentId });
    if (res.ok) {
      await loadAgentDefinitions(state);
      onMutate?.();
    } else {
      state.agentDefsError = res.error ?? "Failed to delete agent definition";
    }
  } catch (err) {
    state.agentDefsError = friendlyError(err);
  }
}

export async function toggleAgentDefinition(
  state: AgentDefinitionsState,
  agentId: string,
  isActive: boolean,
  onMutate?: () => void,
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    await state.client.request<ItemResult>("igloobot.agents.update", { agentId, isActive });
    await loadAgentDefinitions(state);
    onMutate?.();
  } catch (err) {
    state.agentDefsError = friendlyError(err);
  }
}

// --------------- Form helpers ---------------

export function startCreateAgentDef(state: AgentDefinitionsState): void {
  state.agentDefCreateMode = true;
  state.agentDefEditingId = null;
  state.agentDefForm = {
    name: "",
    description: "",
    projectId: state.igProjects[0]?.id ?? "",
    model: null,
    systemPromptOverride: "",
    skills: [],
    bindings: [],
  };
}

export function startEditAgentDef(state: AgentDefinitionsState, def: AgentDefinitionEntry): void {
  state.agentDefCreateMode = false;
  state.agentDefEditingId = def.agentId;
  state.agentDefForm = { ...def };
  // Auto-load identity files when editing
  state.agentDefIdFiles = [];
  state.agentDefIdFileActive = null;
  state.agentDefIdFileDrafts = {};
  void loadAgentDefIdentityFiles(state, def.agentId);
}

export function cancelAgentDefForm(state: AgentDefinitionsState): void {
  state.agentDefCreateMode = false;
  state.agentDefEditingId = null;
  state.agentDefForm = null;
  state.agentDefIdFiles = [];
  state.agentDefIdFileActive = null;
  state.agentDefIdFileDrafts = {};
}

export function updateAgentDefForm(
  state: AgentDefinitionsState,
  field: string,
  value: unknown,
): void {
  if (!state.agentDefForm) {
    return;
  }
  state.agentDefForm = { ...state.agentDefForm, [field]: value };
}

// --------------- Bindings helpers ---------------

export function addAgentDefBinding(state: AgentDefinitionsState): void {
  if (!state.agentDefForm) {
    return;
  }
  const bindings = [...(state.agentDefForm.bindings ?? [])];
  bindings.push({ channel: "", accountId: "" });
  state.agentDefForm = { ...state.agentDefForm, bindings };
}

export function removeAgentDefBinding(state: AgentDefinitionsState, index: number): void {
  if (!state.agentDefForm) {
    return;
  }
  const bindings = [...(state.agentDefForm.bindings ?? [])];
  bindings.splice(index, 1);
  state.agentDefForm = { ...state.agentDefForm, bindings };
}

export function updateAgentDefBinding(
  state: AgentDefinitionsState,
  index: number,
  field: string,
  value: string,
): void {
  if (!state.agentDefForm) {
    return;
  }
  const bindings = [...(state.agentDefForm.bindings ?? [])];
  const binding = { ...bindings[index]! };
  if (field === "channel") {
    binding.channel = value;
  } else if (field === "accountId") {
    binding.accountId = value || undefined;
  } else if (field === "guildId") {
    binding.guildId = value || undefined;
  } else if (field === "teamId") {
    binding.teamId = value || undefined;
  }
  bindings[index] = binding;
  state.agentDefForm = { ...state.agentDefForm, bindings };
}

// --------------- Identity File Overrides ---------------

type IdFilesListResult = {
  ok: boolean;
  data?: { agentId: string; files: IdentityFileInfo[] };
  error?: string;
};

export async function loadAgentDefIdentityFiles(
  state: AgentDefinitionsState,
  agentId: string,
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.agentDefIdFilesLoading = true;
  try {
    const res = await state.client.request<IdFilesListResult>(
      "igloobot.agents.identityFiles.list",
      { agentId },
    );
    if (res.ok && res.data) {
      state.agentDefIdFiles = res.data.files;
      // Initialize drafts from current content
      const drafts: Record<string, string> = {};
      for (const f of res.data.files) {
        drafts[f.fileName] = f.content;
      }
      state.agentDefIdFileDrafts = drafts;
    }
  } catch (err) {
    state.agentDefsError = friendlyError(err);
  } finally {
    state.agentDefIdFilesLoading = false;
  }
}

export function selectAgentDefIdentityFile(state: AgentDefinitionsState, fileName: string): void {
  state.agentDefIdFileActive = fileName;
}

export function updateAgentDefIdentityDraft(
  state: AgentDefinitionsState,
  fileName: string,
  content: string,
): void {
  state.agentDefIdFileDrafts = { ...state.agentDefIdFileDrafts, [fileName]: content };
}

export async function saveAgentDefIdentityFile(
  state: AgentDefinitionsState,
  agentId: string,
  fileName: string,
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  const content = state.agentDefIdFileDrafts[fileName];
  if (content === undefined) {
    return;
  }
  state.agentDefIdFileSaving = true;
  try {
    await state.client.request<SimpleResult>("igloobot.agents.identityFiles.upsert", {
      agentId,
      fileName,
      content,
    });
    // Reload files to update hasOverride indicators
    await loadAgentDefIdentityFiles(state, agentId);
  } catch (err) {
    state.agentDefsError = friendlyError(err);
  } finally {
    state.agentDefIdFileSaving = false;
  }
}

export async function resetAgentDefIdentityFile(
  state: AgentDefinitionsState,
  agentId: string,
  fileName: string,
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.agentDefIdFileSaving = true;
  try {
    await state.client.request<SimpleResult>("igloobot.agents.identityFiles.delete", {
      agentId,
      fileName,
    });
    // Reload to get default template content back
    await loadAgentDefIdentityFiles(state, agentId);
    // Reset active file selection to trigger re-render with new content
    if (state.agentDefIdFileActive === fileName) {
      const updated = state.agentDefIdFiles.find((f) => f.fileName === fileName);
      if (updated) {
        state.agentDefIdFileDrafts = {
          ...state.agentDefIdFileDrafts,
          [fileName]: updated.content,
        };
      }
    }
  } catch (err) {
    state.agentDefsError = friendlyError(err);
  } finally {
    state.agentDefIdFileSaving = false;
  }
}
